import React, { useCallback, useEffect, useRef, useState } from "react";

const TABS = [
  { id: "pnl", label: "PnL" },
  { id: "target", label: "Target Price" },
  { id: "liq", label: "Liquidation Price" },
  { id: "avg", label: "Avg. Entry Price" },
];

// Simplified maintenance margin rate (0.5%).
// Real exchanges use tiered tables; this is the standard calculator default.
const DEFAULT_MMR = 0.005;

function safeFloat(val, fallback = 0) {
  const n = parseFloat(val);
  return Number.isFinite(n) ? n : fallback;
}

function fmt(num, decimals = 4) {
  if (!Number.isFinite(num)) return "--";
  return num.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function filterNumeric(val) {
  return val.replace(/[^0-9.]/g, "").replace(/(\..*?)\./g, "$1");
}

/**
 * FuturesCalculatorModal
 *
 * Props:
 *   open              – boolean
 *   onClose           – () => void
 *   baseSymbol        – e.g. "BTC"
 *   quoteSymbol       – e.g. "USDT"
 *   defaultEntryPrice – pre-fill entry price from order form
 *   defaultSide       – "long" | "short"
 *   defaultLeverage   – number, confirmed leverage from the order form
 *   takerFeeRate      – number 0–1 (e.g. 0.0004) from SelectedCoin.taker_fee
 *
 * Backend API required:
 *   None for PnL / Target Price / Avg Entry Price / Isolated Liquidation —
 *   all are pure client-side math.
 *
 *   Cross Margin Liquidation needs the user's futures wallet balance.
 *   If the backend exposes it, pre-fill the "Wallet Balance" field from:
 *     GET /futures/v1/account  →  { available_balance: "...", wallet_balance: "..." }
 */
export default function FuturesCalculatorModal({
  open,
  onClose,
  quoteSymbol,
  baseSymbol,
  defaultEntryPrice,
  defaultSide,
  defaultLeverage,
  takerFeeRate,
}) {
  const [tab, setTab] = useState("pnl");
  const [side, setSide] = useState("long");
  const [leverage, setLeverage] = useState("10");

  // Shared fields
  const [entryPrice, setEntryPrice] = useState("");
  const [quantity, setQuantity] = useState("");

  // PnL tab
  const [exitPrice, setExitPrice] = useState("");
  const [feeRate, setFeeRate] = useState("0.04"); // taker fee % (editable)

  // Target Price tab
  const [roi, setRoi] = useState("");

  // Liquidation tab
  const [marginMode, setMarginMode] = useState("Isolated");
  const [walletBalance, setWalletBalance] = useState("");
  const [addMargin, setAddMargin] = useState("");

  // Avg Entry tab
  const [avgPositions, setAvgPositions] = useState([{ price: "", amount: "" }]);

  const [results, setResults] = useState(null);

  // Track whether the modal was previously open so we only seed entry price
  // on the closed→open transition, not on every real-time price tick.
  const wasOpenRef = useRef(false);

  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;

    if (!open) return;

    const lev = defaultLeverage != null && defaultLeverage !== "" ? String(defaultLeverage) : "10";
    const fr = takerFeeRate != null ? String((takerFeeRate * 100).toFixed(4)) : "0.04";
    setLeverage(lev);
    setFeeRate(fr);
    setSide(defaultSide || "long");

    // Seed entry price only once when the modal first opens.
    // While open, the user owns the field — live price changes are ignored.
    if (justOpened) {
      const ep = defaultEntryPrice != null && defaultEntryPrice !== "" ? String(defaultEntryPrice) : "";
      setEntryPrice(ep);
      setResults(null);
    }
  }, [open, defaultEntryPrice, defaultSide, defaultLeverage, takerFeeRate]);

  // ── Tab scroll arrows ─────────────────────────────────────────────────────
  const tabsRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const checkScroll = useCallback(() => {
    const el = tabsRef.current;
    if (!el) return;
    setShowLeftArrow(el.scrollLeft > 0);
    setShowRightArrow(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  useEffect(() => {
    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => window.removeEventListener("resize", checkScroll);
  }, [open, checkScroll]);

  const scroll = (direction) => {
    tabsRef.current?.scrollBy({
      left: direction === "left" ? -150 : 150,
      behavior: "smooth",
    });
  };

  if (!open) return null;

  const q = quoteSymbol || "USDT";
  const b = baseSymbol || "BTC";

  // ── Calculations ──────────────────────────────────────────────────────────
  const handleCalculate = () => {
    const ep = safeFloat(entryPrice);
    const lev = Math.max(1, safeFloat(leverage, 1));
    const qty = safeFloat(quantity);
    const fr = safeFloat(feeRate) / 100; // % → decimal

    if (tab === "pnl") {
      const exP = safeFloat(exitPrice);
      if (ep <= 0 || exP <= 0 || qty <= 0) return;

      // Initial margin = position value / leverage
      const initialMargin = (qty * ep) / lev;

      // Open and close fees (both at taker rate)
      const openFee = qty * ep * fr;
      const closeFee = qty * exP * fr;

      // Raw directional PnL
      const rawPnl = side === "long" ? qty * (exP - ep) : qty * (ep - exP);

      // Net PnL after fees
      const pnl = rawPnl - openFee - closeFee;

      // ROE = net PnL / initial margin × 100
      const roe = initialMargin > 0 ? (pnl / initialMargin) * 100 : 0;

      setResults({ initialMargin, openFee, closeFee, pnl, roe });

    } else if (tab === "target") {
      if (ep <= 0) return;
      const targetRoi = safeFloat(roi);

      // Derive target exit price that achieves targetRoi% ROE (fee-inclusive):
      //   (rawPnl - openFee - closeFee) / initialMargin = targetRoi / 100
      //   qty * (tp - ep) - qty*ep*fr - qty*tp*fr = (qty*ep/lev) * targetRoi/100   [long]
      //   tp * (1 - fr) = ep + ep*fr + ep*targetRoi/(100*lev)
      //   tp = ep * (1 + fr + targetRoi/(100*lev)) / (1 - fr)
      let targetPrice;
      if (side === "long") {
        targetPrice = ep * (1 + fr + targetRoi / (100 * lev)) / (1 - fr);
      } else {
        // qty*(ep-tp) - qty*ep*fr - qty*tp*fr = initMgn * targetRoi/100
        // ep - tp - ep*fr - tp*fr = ep*targetRoi/(100*lev)
        // ep*(1-fr) - tp*(1+fr) = ep*targetRoi/(100*lev)
        // tp = ep*(1 - fr - targetRoi/(100*lev)) / (1 + fr)
        targetPrice = ep * (1 - fr - targetRoi / (100 * lev)) / (1 + fr);
      }

      setResults({ targetPrice });

    } else if (tab === "liq") {
      if (ep <= 0 || qty <= 0) return;

      const mmr = DEFAULT_MMR;
      const am = safeFloat(addMargin); // extra margin added to position

      if (marginMode === "Isolated") {
        // Isolated long:  liqPrice = ep - (isolatedMargin - mmr * ep * qty) / qty
        // Isolated short: liqPrice = ep + (isolatedMargin - mmr * ep * qty) / qty
        // where isolatedMargin = qty * ep / lev + am
        const isolatedMargin = (ep * qty) / lev + am;
        const maintenanceMarginVal = mmr * ep * qty;
        let liqPrice;
        if (side === "long") {
          liqPrice = ep - (isolatedMargin - maintenanceMarginVal) / qty;
        } else {
          liqPrice = ep + (isolatedMargin - maintenanceMarginVal) / qty;
        }
        setResults({ liqPrice: Math.max(0, liqPrice) });

      } else {
        // Cross margin — needs the user's total futures wallet balance.
        // Formula:
        //   Long:  liqPrice = (qty*ep - wb - am) / (qty * (1 - mmr))
        //   Short: liqPrice = (qty*ep + wb + am) / (qty * (1 + mmr))
        //
        // Backend API to pre-fill wallet balance:
        //   GET /futures/v1/account → { wallet_balance: "...", available_balance: "..." }
        const wb = safeFloat(walletBalance);
        if (wb <= 0) {
          setResults({ liqPriceCrossNeedsBalance: true });
          return;
        }
        let liqPrice;
        if (side === "long") {
          liqPrice = (qty * ep - wb - am) / (qty * (1 - mmr));
        } else {
          liqPrice = (qty * ep + wb + am) / (qty * (1 + mmr));
        }
        setResults({ liqPrice: Math.max(0, liqPrice) });
      }

    } else if (tab === "avg") {
      let totalValue = 0;
      let totalAmount = 0;
      for (const p of avgPositions) {
        const pr = safeFloat(p.price);
        const am = safeFloat(p.amount);
        totalValue += pr * am;
        totalAmount += am;
      }
      const avgEntryPrice = totalAmount > 0 ? totalValue / totalAmount : 0;
      setResults({ avgEntryPrice, totalAmount });
    }
  };

  const isCalculateDisabled = () => {
    if (tab === "pnl") return !entryPrice || !exitPrice || !quantity;
    if (tab === "target") return !entryPrice || !roi;
    if (tab === "liq") return !entryPrice || !quantity;
    if (tab === "avg") return avgPositions.some((p) => !p.price || !p.amount);
    return true;
  };

  const handleAddPosition = () => {
    setAvgPositions([...avgPositions, { price: "", amount: "" }]);
  };

  const handleDeletePosition = (index) => {
    if (avgPositions.length <= 1) return;
    setAvgPositions(avgPositions.filter((_, i) => i !== index));
    setResults(null);
  };

  const updateAvgPosition = (index, field, value) => {
    const next = [...avgPositions];
    next[index] = { ...next[index], [field]: value };
    setAvgPositions(next);
  };

  // ── Form fields ───────────────────────────────────────────────────────────
  const renderFormFields = () => {
    switch (tab) {
      case "pnl":
        return (
          <>
            <label className="agcx-ft-calc-field">
              <span>Leverage</span>
              <div className="agcx-ft-calc-input">
                <input value={leverage} inputMode="decimal" onChange={(e) => setLeverage(filterNumeric(e.target.value))} />
                <span>x</span>
              </div>
            </label>
            <label className="agcx-ft-calc-field">
              <span>Entry Price</span>
              <div className="agcx-ft-calc-input">
                <input value={entryPrice} inputMode="decimal" onChange={(e) => setEntryPrice(filterNumeric(e.target.value))} placeholder="0.00" />
                <span>{q}</span>
              </div>
            </label>
            <label className="agcx-ft-calc-field">
              <span>Exit Price</span>
              <div className="agcx-ft-calc-input">
                <input value={exitPrice} inputMode="decimal" onChange={(e) => setExitPrice(filterNumeric(e.target.value))} placeholder="0.00" />
                <span>{q}</span>
              </div>
            </label>
            <label className="agcx-ft-calc-field">
              <span>Quantity</span>
              <div className="agcx-ft-calc-input">
                <input value={quantity} inputMode="decimal" onChange={(e) => setQuantity(filterNumeric(e.target.value))} placeholder="0.00" />
                <span>{b}</span>
              </div>
            </label>
            <label className="agcx-ft-calc-field">
              <span>Fee Rate (Taker)</span>
              <div className="agcx-ft-calc-input">
                <input value={feeRate} inputMode="decimal" onChange={(e) => setFeeRate(filterNumeric(e.target.value))} placeholder="0.04" />
                <span>%</span>
              </div>
            </label>
          </>
        );
      case "target":
        return (
          <>
            <label className="agcx-ft-calc-field">
              <span>Leverage</span>
              <div className="agcx-ft-calc-input">
                <input value={leverage} inputMode="decimal" onChange={(e) => setLeverage(filterNumeric(e.target.value))} />
                <span>x</span>
              </div>
            </label>
            <label className="agcx-ft-calc-field">
              <span>Entry Price</span>
              <div className="agcx-ft-calc-input">
                <input value={entryPrice} inputMode="decimal" onChange={(e) => setEntryPrice(filterNumeric(e.target.value))} placeholder="0.00" />
                <span>{q}</span>
              </div>
            </label>
            <label className="agcx-ft-calc-field">
              <span>ROI %</span>
              <div className="agcx-ft-calc-input">
                <input value={roi} inputMode="decimal" onChange={(e) => setRoi(filterNumeric(e.target.value))} placeholder="0.00" />
                <span>%</span>
              </div>
            </label>
            <label className="agcx-ft-calc-field">
              <span>Fee Rate (Taker)</span>
              <div className="agcx-ft-calc-input">
                <input value={feeRate} inputMode="decimal" onChange={(e) => setFeeRate(filterNumeric(e.target.value))} placeholder="0.04" />
                <span>%</span>
              </div>
            </label>
          </>
        );
      case "liq":
        return (
          <>
            <div className="agcx-ft-calc-field">
              <select className="agcx-ft-calc-select" value={marginMode} onChange={(e) => { setMarginMode(e.target.value); setResults(null); }}>
                <option value="Isolated">Isolated</option>
                <option value="Cross">Cross</option>
              </select>
            </div>
            <label className="agcx-ft-calc-field">
              <span>Leverage</span>
              <div className="agcx-ft-calc-input">
                <input value={leverage} inputMode="decimal" onChange={(e) => setLeverage(filterNumeric(e.target.value))} />
                <span>x</span>
              </div>
            </label>
            <label className="agcx-ft-calc-field">
              <span>Size</span>
              <div className="agcx-ft-calc-input">
                <input value={quantity} inputMode="decimal" onChange={(e) => setQuantity(filterNumeric(e.target.value))} placeholder="0.00" />
                <span>{b}</span>
              </div>
            </label>
            <label className="agcx-ft-calc-field">
              <span>Entry Price</span>
              <div className="agcx-ft-calc-input">
                <input value={entryPrice} inputMode="decimal" onChange={(e) => setEntryPrice(filterNumeric(e.target.value))} placeholder="0.00" />
                <span>{q}</span>
              </div>
            </label>
            {marginMode === "Cross" && (
              <label className="agcx-ft-calc-field">
                <span>Wallet Balance</span>
                <div className="agcx-ft-calc-input">
                  <input value={walletBalance} inputMode="decimal" onChange={(e) => setWalletBalance(filterNumeric(e.target.value))} placeholder="0.00" />
                  <span>{q}</span>
                </div>
              </label>
            )}
            <label className="agcx-ft-calc-field">
              <span>Add Margin (Optional)</span>
              <div className="agcx-ft-calc-input">
                <input value={addMargin} inputMode="decimal" onChange={(e) => setAddMargin(filterNumeric(e.target.value))} placeholder="0.00" />
                <span>{q}</span>
              </div>
            </label>
          </>
        );
      case "avg":
        return (
          <div className="agcx-ft-calc-avg-list">
            <div className="agcx-ft-calc-avg-head">
              <span className="avg-head-label">No.</span>
              <span className="avg-head-label">Entry Price</span>
              <span className="avg-head-label">Entry Amount</span>
              <span className="avg-head-label"></span>
            </div>
            {avgPositions.map((pos, idx) => (
              <div key={idx} className="agcx-ft-calc-avg-row">
                <span className="avg-no">{idx + 1}</span>
                <div className="agcx-ft-calc-input">
                  <input
                    value={pos.price}
                    inputMode="decimal"
                    onChange={(e) => updateAvgPosition(idx, "price", filterNumeric(e.target.value))}
                    placeholder="Price"
                    aria-label={`Position ${idx + 1} Entry Price`}
                  />
                  <span>{q}</span>
                </div>
                <div className="agcx-ft-calc-input">
                  <input
                    value={pos.amount}
                    inputMode="decimal"
                    onChange={(e) => updateAvgPosition(idx, "amount", filterNumeric(e.target.value))}
                    placeholder="Amount"
                    aria-label={`Position ${idx + 1} Entry Amount`}
                  />
                  <span>{b}</span>
                </div>
                <div className="avg-action-col">
                  {avgPositions.length > 1 && (
                    <button type="button" className="agcx-ft-calc-avg-delete" aria-label="Delete position" onClick={() => handleDeletePosition(idx)}>
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  )}
                </div>
              </div>
            ))}
            <button type="button" className="agcx-ft-calc-add-btn" onClick={handleAddPosition}>
              <i className="ri-add-circle-line"></i> Add a Position
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  // ── Results ───────────────────────────────────────────────────────────────
  const renderResults = () => {
    switch (tab) {
      case "pnl":
        return (
          <>
            <div className="agcx-ft-calc-out-row">
              <span>Initial Margin</span>
              <span>{results ? fmt(results.initialMargin) : "--"} {q}</span>
            </div>
            <div className="agcx-ft-calc-out-row">
              <span>Open Fee</span>
              <span className={results ? "text-danger" : ""}>{results ? fmt(results.openFee) : "--"} {q}</span>
            </div>
            <div className="agcx-ft-calc-out-row">
              <span>Close Fee</span>
              <span className={results ? "text-danger" : ""}>{results ? fmt(results.closeFee) : "--"} {q}</span>
            </div>
            <div className="agcx-ft-calc-out-row">
              <span>PnL</span>
              <span className={results?.pnl > 0 ? "text-green" : results?.pnl < 0 ? "text-danger" : ""}>
                {results ? fmt(results.pnl) : "--"} {q}
              </span>
            </div>
            <div className="agcx-ft-calc-out-row">
              <span>ROE %</span>
              <span className={results?.roe > 0 ? "text-green" : results?.roe < 0 ? "text-danger" : ""}>
                {results ? `${fmt(results.roe, 2)}%` : "--"}
              </span>
            </div>
          </>
        );
      case "target":
        return (
          <div className="agcx-ft-calc-out-row">
            <span>Target Price</span>
            <span>{results ? fmt(results.targetPrice) : "--"} {q}</span>
          </div>
        );
      case "liq":
        if (results?.liqPriceCrossNeedsBalance) {
          return (
            <div className="agcx-ft-calc-out-row">
              <span>Liquidation Price</span>
              <span>Enter wallet balance</span>
            </div>
          );
        }
        return (
          <>
            <div className="agcx-ft-calc-out-row">
              <span>Liquidation Price</span>
              <span>{results ? fmt(results.liqPrice) : "--"} {q}</span>
            </div>
            <p className="agcx-ft-calc-liq-note">
              MMR = {(DEFAULT_MMR * 100).toFixed(1)}%.{" "}
              {marginMode === "Cross"
                ? "Cross: your full futures wallet balance backs this position."
                : "Isolated: only position margin is at risk."}{" "}
              Funding rates and fee deductions are excluded.
            </p>
          </>
        );
      case "avg":
        return (
          <>
            <div className="agcx-ft-calc-out-row">
              <span>Avg. Entry Price</span>
              <span>{results ? fmt(results.avgEntryPrice) : "--"} {q}</span>
            </div>
            <div className="agcx-ft-calc-out-row">
              <span>Total Amount</span>
              <span>{results ? fmt(results.totalAmount) : "--"} {b}</span>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="agcx-ft-modal-root" role="dialog" aria-modal="true" aria-labelledby="agcx-ft-calc-title">
      <button type="button" className="agcx-ft-modal-backdrop" aria-label="Close" onClick={onClose} />
      <div className={`agcx-ft-modal-panel agcx-ft-modal-panel--calc ${tab === "avg" ? "agcx-ft-modal-panel--avg" : ""}`}>
        <div className="agcx-ft-modal-head">
          <h2 id="agcx-ft-calc-title" className="agcx-ft-modal-title">
            Calculator
          </h2>
          <button type="button" className="agcx-ft-modal-close" aria-label="Close" onClick={onClose}>
            <i className="ri-close-line"></i>
          </button>
        </div>
        <div className="agcx-ft-calc-tabs-wrapper">
          {showLeftArrow && (
            <button type="button" className="agcx-ft-calc-tab-arrow left" onClick={() => scroll("left")}>
              <i className="ri-arrow-left-s-line"></i>
            </button>
          )}
          <div className="agcx-ft-calc-tabs" ref={tabsRef} onScroll={checkScroll}>
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`agcx-ft-calc-tab${tab === t.id ? " is-active" : ""}`}
                onClick={() => { setTab(t.id); setResults(null); }}
              >
                {t.label}
              </button>
            ))}
          </div>
          {showRightArrow && (
            <button type="button" className="agcx-ft-calc-tab-arrow right" onClick={() => scroll("right")}>
              <i className="ri-arrow-right-s-line"></i>
            </button>
          )}
        </div>
        <div className="agcx-ft-modal-body agcx-ft-calc-body">
          <div className="agcx-ft-calc-grid">
            <div className="agcx-ft-calc-col agcx-ft-calc-col--form">
              {tab !== "avg" && (
                <div className="agcx-ft-calc-longshort">
                  <button type="button" className={side === "long" ? "is-on" : ""} onClick={() => { setSide("long"); setResults(null); }}>
                    Long
                  </button>
                  <button type="button" className={side === "short" ? "is-on" : ""} onClick={() => { setSide("short"); setResults(null); }}>
                    Short
                  </button>
                </div>
              )}

              {renderFormFields()}

              <button
                type="button"
                className="agcx-ft-btn agcx-ft-btn--primary agcx-ft-btn--block"
                disabled={isCalculateDisabled()}
                onClick={handleCalculate}
              >
                Calculate
              </button>
            </div>

            <div className="agcx-ft-calc-col agcx-ft-calc-col--out">
              <div className="agcx-ft-calc-results-card">
                {renderResults()}
                {tab === "liq" && !results && (
                  <p className="agcx-ft-calc-liq-note">When calculating liquidation price, we ignore current position and fees.</p>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="agcx-ft-calc-note">
          <i className="ri-information-line" aria-hidden />
          <span>
            The calculation is for reference only. Actual results may differ due to funding rates, price
            impact, and fee tiers. Maintenance margin rate is simplified at {(DEFAULT_MMR * 100).toFixed(1)}%.{" "}
            .
          </span>
        </div>
      </div>
    </div>
  );
}
